import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode, RefObject } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

import { getElementContent, getPageContent, hasSSLCertificate } from "@pm2/api";
import { parseElementXML, parsePageXML } from "@pm2/content-engine";
import { useIsServerSide } from "@pm2/react-utils";
import type {
  PM2SitemapState,
  PM2SitemapStore,
  UrlPattern,
} from "@pm2/sitemap-utils";
import {
  extractPatternFromUrl,
  getPageDescriptorByPath,
  isPartOfMegaMenu,
  openMegaMenuAtBreadcrumb,
  recursivelyFindResourceById,
  getResourcesForPage,
  getPagePathById,
  findPageDescriptorById,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import { pm2ConfigStore, type PM2ConfigStore } from "@pm2/store";
import { nProgressStore } from "@pm2/ui-components";
import { usePm2Modal } from "@pm2/ui-lightbox";
import type {
  LinkProps,
  LocationData,
  NavigationManagerContextType,
} from "@provider/navigation";
import {
  NavigationManagerProvider,
  useLocation,
  useNavigationManager,
} from "@provider/navigation";
import type { UserIdentity } from "@provider/user-identity";
import { useUserIdentityState } from "@provider/user-identity";
import { breakpoints } from "@ui/css";
import { createCacheWrapper } from "@utils/cache";
import { isPlatformNodeJS, reduceNodeOffset } from "@utils/common";
import { HOURS_TO_MS } from "@utils/constants";
import { useMatchBreakpoint, useStateRef } from "@utils/react";

const retainedSearchParams = ["revisionId", "revisionTitle"];

type Props = {
  basePath?: string;
  children: ReactNode | ReactNode[];
};

const BasePathContext = createContext<string>("");

type BrowserSyncContextValue = {
  flush: (href: string) => void;
  subscribe: (cb: () => void) => () => void;
};
const BrowserSyncContext = createContext<BrowserSyncContextValue>({
  flush: () => {
    throw new Error("BrowserSyncContext: no integration provided");
  },
  subscribe: () => {
    throw new Error("BrowserSyncContext: no integration provided");
  },
});

/**
 * @note - combined in-memory and browser history routing
 *
 * PM2 has some very specific requirements for the navigation provider, where
 * we need to ensure that browser history is honered and triggers updates in
 * content - but the previously rendered page is retained until all relevant
 * data has been fetched to actually render the next one
 *
 * in order to achieve this, we have made this custom implementation which is
 * essentially a combination between an in-memory navigation and a browser
 * history that can be flushed to the in-memory state
 *
 * @note - ignores unwanted searchParams
 *
 * in the past we experienced DoS attacks where attackers had figured out that
 * they could bypass our caching by applying random query strings to URLs and
 * thereby force the system to re-render the same pages infinitely
 */
export function PM2NavigationProvider(props: Props) {
  const [href, setHref] = useStateRef(window.location.href);
  const subscribersRef = useRef(new Set<() => void>());
  const browserSubscribersRef = useRef(new Set<() => void>());

  const browserSyncContext = useMemo(
    (): BrowserSyncContextValue => ({
      flush: (href) => setHref(href),
      subscribe: (cb) => {
        const uniqueCb = () => cb();

        browserSubscribersRef.current.add(uniqueCb);

        return () => browserSubscribersRef.current.delete(uniqueCb);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const managerContext = useMemo((): NavigationManagerContextType => {
    return {
      getPath: () => extractPm2PathFromHref(href.current, props.basePath),
      subscribe: (cb) => {
        const uniqueCb = () => cb();

        subscribersRef.current.add(uniqueCb);

        return () => subscribersRef.current.delete(uniqueCb);
      },

      push: (nextPath) => {
        const nextUrl = new URL(
          applyRetainableSearchParams(managerContext.getPath(), nextPath),
          window.location.origin,
        );
        if (props.basePath && !nextUrl.pathname.startsWith(props.basePath)) {
          nextUrl.pathname = `${props.basePath}${nextUrl.pathname}`;
        }

        // There's no need to pollute our history if we're already at
        // the place we're pushing to
        if (nextUrl.href === href.current || nextUrl.href === `${href}/`) {
          return;
        }

        history.pushState(null, "", nextUrl.href.replace(nextUrl.origin, ""));

        // manually trigger subscribers when updating state (as pushState isn't
        // implemented as an event)
        browserSubscribersRef.current.forEach((cb) => cb());
      },
      replace: (nextPath) => {
        const nextUrl = new URL(
          applyRetainableSearchParams(managerContext.getPath(), nextPath),
          window.location.origin,
        );

        if (props.basePath && !nextUrl.pathname.startsWith(props.basePath)) {
          nextUrl.pathname = `${props.basePath}${nextUrl.pathname}`;
        }

        history.replaceState(
          null,
          "",
          nextUrl.href.replace(nextUrl.origin, ""),
        );

        // manually trigger subscribers when updating state (as
        // pushState isn't implemented as an event)
        browserSubscribersRef.current.forEach((cb) => cb());
      },
      back: () => {
        history.back();
      },

      enhanceLink: (props, ref) => (
        <PM2Link ref={ref} {...props} prevPath={managerContext.getPath()} />
      ),
    };
  }, [href, props.basePath]);

  // subscribe to the popState event, so that we can handle built-in browser
  // back/forwards navigation
  useEffect(() => {
    const handlePopState = () => {
      browserSubscribersRef.current.forEach((cb) => cb());
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    subscribersRef.current.forEach((cb) => cb());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [href.current]);

  return (
    <BasePathContext.Provider value={props.basePath ?? ""}>
      <BrowserSyncContext.Provider value={browserSyncContext}>
        <NavigationManagerProvider manager={managerContext}>
          {props.children}
        </NavigationManagerProvider>
      </BrowserSyncContext.Provider>
    </BasePathContext.Provider>
  );
}

function PM2Link({
  onClick,
  prevPath,
  ...props
}: LinkProps & {
  prevPath: string;
  ref?: RefObject<HTMLAnchorElement | null>;
}) {
  const queryClient = useQueryClient();
  const user = useUserIdentityState((state) => state.user);
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const sitemap = pm2SitemapStore.useSelector((state) => state);
  const pm2Modal = usePm2Modal();
  const location = useLocation();
  const navigation = useNavigationManager();
  const isMobile = useMatchBreakpoint(breakpoints.mobile.andDown());

  const urlPattern =
    props.href !== undefined ? extractPatternFromUrl(props.href) : undefined;
  const parsedHref = parseHref(sitemap, location, urlPattern);
  const target = props.target;

  const basePath = useContext(BasePathContext);
  const href = resolveHref(basePath, prevPath, parsedHref);

  const handleNavigate = useCallback(
    async (evt: React.MouseEvent<HTMLAnchorElement>) => {
      await onClick?.(evt);

      if (!href) {
        // if no url was provided, then we cannot handle the link - bail out
        // and revert to built-in browser logic for href-less links
        return;
      }

      if (evt.isDefaultPrevented() && evt.isTrusted) {
        // if default handling was prevented, then avoid doing any further
        // processing, so as to allow integrations to disable navigation as
        // you would with native links.
        //
        // NB: For click events triggered programatically (e.g. through
        // triggerEventOnContainerClick()) we need to call .preventDefault()
        // on our custom event to ensure that the default link logic isn't
        // triggered. As such we should ignore isDefaultPrevented() on these
        // types of link interactions.
        return;
      }

      if (evt.metaKey || evt.ctrlKey) {
        // if user is holding down modifier keys, then bail out and let the
        // browser use built-in navigation concepts to open links in new tabs
        return;
      }

      if (target === "_blank") {
        // if target is set to a new window, then let the browser do it's
        // thing in order to enable default handling of the link
        return;
      }

      if (target === "_lightbox") {
        evt.preventDefault();

        const contentPromise = fetchLightboxContent(
          queryClient,
          configStore,
          sitemapStore,
          location,
          user,
          href,
          urlPattern,
        );

        const ast = contentPromise.then((content) => content.ast);
        const options = contentPromise.then((content) => ({
          disableSpeechSynthesisButton: !!content.speechFile,
        }));

        pm2Modal.fromAST(ast, options);
        return;
      }

      const url = new URL(href, window.location.href);

      if (url.host !== window.location.host) {
        // if we're navigating cross-domain, then let the default anchor
        // handler do it's thing (so that we honor requests to open in new
        // window out of the box)
        return;
      }

      if (url.pathname.startsWith(configStore.getState().baseUrl)) {
        // if the pathname has been pre-fixed with the baseUrl already, then
        // prevent duplicate baseUrl injection
        url.pathname = url.pathname.replace(configStore.getState().baseUrl, "");
      }

      evt.preventDefault();

      // In case we're navigating to an anchor on the current page, then do so
      // without triggering an actual navigation!
      if (
        url.hash &&
        url.pathname === location.pathname &&
        url.searchParams.toString() === location.search.toString()
      ) {
        navigation.replace(`${url.pathname}${url.search}${url.hash}`);

        const hash = url.hash.replace("#", "");
        const element =
          document.querySelector<HTMLElement>(`[name="${hash}"]`) ??
          document.getElementById(hash);

        if (element) {
          window.scrollTo({
            top: reduceNodeOffset(element).top - 100,
            behavior: "smooth",
          });
        }

        return;
      }

      try {
        // if we get here, it means that navigation must begin on some level;
        // make sure that the progress bar is displayed
        nProgressStore.dispatch(nProgressStore.actions.enable());

        // in the background begin fetching the page descriptor, so that we
        // can determine if the page is part of a megamenu (and in that case
        // simply expand the mega menu)
        const pagePath = await resolvePagePath(
          configStore,
          sitemap,
          location,
          urlPattern,
        );
        const pageDescriptor =
          pagePath !== undefined
            ? await getPageDescriptorByPath(configStore, sitemapStore, pagePath)
            : undefined;

        if (pageDescriptor && isPartOfMegaMenu(sitemap, pageDescriptor)) {
          // ... otherwise open the requested page inside the mega menu, so
          // that the user can simply browse pages from there
          openMegaMenuAtBreadcrumb(configStore, sitemapStore, pageDescriptor, {
            isMobile: !!isMobile,
          });
        } else {
          navigation.push(`${url.pathname}${url.search}${url.hash}`);
        }
      } finally {
        // trigger navigation end event, as we've finished handling the
        // desired action
        nProgressStore.dispatch(nProgressStore.actions.disable());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props,
      target,
      href,
      location.pathname,
      location.search,
      configStore,
      sitemapStore,
      sitemap,
      user,
      pm2Modal,
      navigation,
      queryClient,
      isMobile,
    ],
  );

  return (
    <a ref={props.ref} {...props} href={href} onClick={handleNavigate}>
      {props.children}
    </a>
  );
}

const cache = createCacheWrapper({
  evictAfterMs: 1 * HOURS_TO_MS,
});

/**
 * custom location hook that resolves the currently active path within the
 * browser, and allows flushing it to the navigation provider state
 */
export function useBrowserLocation(): LocationData & {
  flush: () => void;
} {
  const serverSide = useIsServerSide();
  const location = useLocation();
  const basePath = useContext(BasePathContext);
  const { flush, subscribe } = useContext(BrowserSyncContext);

  const getSnapshot = () => {
    if (serverSide) {
      return undefined;
    }

    const path = extractPm2PathFromHref(window.location.href, basePath);

    return cache(path, () => {
      const url = new URL(path, "http://localhost/");
      return {
        href: window.location.href,
        asPath: path,
        pathname: url.pathname,
        search: url.search,
        searchParams: url.searchParams,
        hash: url.hash,
      };
    }).value;
  };

  const locationData = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );

  return useMemo(
    () =>
      locationData
        ? {
            ...locationData,

            flush: () => {
              flush(locationData.href);
            },
          }
        : {
            ...location,
            flush: () => {
              // flushing becomes a no-operation on server side environments
              // where browser location isn't available
            },
          },
    [flush, location, locationData],
  );
}

export function extractPm2PathFromHref(
  href: string,
  basePath: string | undefined,
): string {
  const url = new URL(href, "http://localhost/");

  // strip away basePath from the given link
  if (basePath && url.pathname.startsWith(basePath)) {
    url.pathname = url.pathname.substring(basePath.length);
  }

  // strip away all un-wanted searchParams
  for (const searchParamName of url.searchParams.keys()) {
    switch (searchParamName) {
      case "q":
      case "resource":
      case "dashboard":
      case "utm_source":
      case "utm_medium":
      case "utm_campaign":
      case "query":
      case "view":
      case "item":
      case "group":
      case "filters":
      case "revisionId":
      case "revisionTitle":
        // allow whitelist only
        break;

      default:
        url.searchParams.delete(searchParamName);
    }
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

function parseHref(
  sitemap: PM2SitemapState,
  location: LocationData,
  urlPattern: UrlPattern | undefined,
): string | undefined {
  if (urlPattern === undefined) {
    return undefined;
  }

  if ("elementId" in urlPattern) {
    return `/learningObject/${urlPattern.elementId}`;
  }

  if ("pageId" in urlPattern) {
    const pageDescriptor = findPageDescriptorById(sitemap, urlPattern.pageId);
    return pageDescriptor ? pageDescriptor.path : `/page/${urlPattern.pageId}`;
  }

  if ("resourceId" in urlPattern) {
    return `${location.pathname}?resource=${urlPattern.resourceId}`;
  }

  return urlPattern.href;
}

function resolveHref(
  basePath: string,
  prevPath: string,
  nextHref: string | undefined,
): string | undefined {
  if (nextHref === undefined) {
    return undefined;
  }

  if (nextHref.startsWith("/")) {
    return applyRetainableSearchParams(prevPath, `${basePath}${nextHref}`);
  } else {
    if (canParseUrl(nextHref)) {
      return nextHref;
    }

    try {
      const url = new URL(
        nextHref,
        isPlatformNodeJS() ? "http://localhost" : window.location.href,
      );
      const protocol = hasSSLCertificate(url.hostname) ? "https://" : "http://";

      return `${protocol}${nextHref}`;
    } catch (err) {
      console.error(err);
      return nextHref;
    }
  }
}

function canParseUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

async function resolvePagePath(
  configStore: PM2ConfigStore,
  sitemap: PM2SitemapState,
  location: LocationData,
  urlPattern: UrlPattern | undefined,
): Promise<string | undefined> {
  if (urlPattern === undefined) {
    return undefined;
  }

  if ("elementId" in urlPattern) {
    return undefined;
  }

  if ("pageId" in urlPattern) {
    return getPagePathById(configStore, sitemap, urlPattern.pageId);
  }

  if ("resourceId" in urlPattern) {
    return `${location.pathname}?resource=${urlPattern.resourceId}`;
  }

  return new URL(urlPattern.href, window.location.href).pathname;
}

async function fetchLightboxContent(
  queryClient: QueryClient,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  location: LocationData,
  user: UserIdentity | undefined | null,
  href: string,
  urlPattern: UrlPattern | undefined,
) {
  const configState = configStore.getState();

  // start by loading the AST of the content to be printed
  const ast = await (async () => {
    if (urlPattern) {
      if ("elementId" in urlPattern) {
        return parseElementXML(
          queryClient,
          await getElementContent(configState.config.id, urlPattern.elementId, {
            previewMode: configState.previewMode,
          }),
          user,
        );
      }

      if ("pageId" in urlPattern) {
        return parsePageXML(
          queryClient,
          await getPageContent(configState.config.id, urlPattern.pageId, {
            previewMode: configState.previewMode,
          }),
          user,
        );
      }

      if ("resourceId" in urlPattern) {
        const pageDescriptors = await getResourcesForPage(
          configStore,
          sitemapStore,
          await getPageDescriptorByPath(
            configStore,
            sitemapStore,
            location.pathname,
          ),
        );
        const descriptor = recursivelyFindResourceById(
          pageDescriptors,
          urlPattern.resourceId,
        );

        if (descriptor) {
          const content =
            descriptor.kind === "ContentElement"
              ? await getElementContent(
                  configState.config.id,
                  urlPattern.resourceId,
                  {
                    previewMode: configState.previewMode,
                  },
                )
              : await getPageContent(
                  configState.config.id,
                  urlPattern.resourceId,
                  {
                    previewMode: configState.previewMode,
                  },
                );

          if (content) {
            return parsePageXML(queryClient, content, user);
          }
        }
      }
    }

    // if we get here, try to resolve the page by the qualified href
    const pageHref =
      urlPattern && "href" in urlPattern ? urlPattern.href : href;
    const pageDescriptor = await getPageDescriptorByPath(
      configStore,
      sitemapStore,
      pageHref,
    );

    if (!pageDescriptor) {
      return [];
    }

    return parsePageXML(
      queryClient,
      await getPageContent(configState.config.id, pageDescriptor.id, {
        previewMode: configState.previewMode,
      }),
      user,
    );
  })();

  // based on the AST, determine if a speechFile should be added to the modal
  const speechFile = (() => {
    if (ast.length === 1 && ast[0] && "pm2" in ast[0]) {
      return ast[0].pm2.speechFileUrl;
    }
  })();

  return {
    ast,
    speechFile,
  };
}

function applyRetainableSearchParams<T extends string | undefined>(
  prevPath: string,
  nextPath: T,
): T {
  if (!nextPath) {
    return nextPath;
  }

  const previousSearchParams = new URL(prevPath, window.location.origin)
    .searchParams;

  const nextUrl = new URL(nextPath, window.location.origin);

  for (const param of retainedSearchParams ?? []) {
    if (!nextUrl.searchParams.has(param)) {
      const previousValue = previousSearchParams?.get(param);

      if (previousValue) {
        nextUrl.searchParams.set(param, previousValue);
      }
    }
  }

  return nextUrl.href.replace(window.location.origin, "") as T;
}
